import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Zap, 
  Star, 
  Lock,
  Settings,
  Info,
  Crown,
  Bell,
  Target,
  TrendingUp,
  AlertTriangle,
  Calendar,
  CheckCircle2,
  History,
  Clock,
  XCircle,
  ArrowUpRight,
  ArrowDownRight,
  Check,
  X,
  Loader2,
  DollarSign
} from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import TradingViewLink from '../components/TradingViewLink';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import OnboardingTutorial from '../components/OnboardingTutorial';
import { SCAN_AUTO_SLIDES } from '../config/tutorialSlides';
import { getSymbolName } from '../utils/symbolNames';
import HowItWorks from '../components/HowItWorks';
import GuestActionBanner from '../components/GuestActionBanner';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ScanAuto() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  
  const [autoScanSettings, setAutoScanSettings] = useState({
    enabled: true,
    tier: 'pro',
    max_assets: 3,
    selected_assets: [],
    entry_type: 'optimal',
    risk_profile: 'balanced',
    trade_type: 'INTRADAY',
    notify_scan_summary: true,
    margin: 100,  // Default margin $100
    can_modify: true,
    days_until_modification: 0,
    has_stock_assets: false,
    weekend_warning: null
  });
  
  const [availableAssets, setAvailableAssets] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [showAssetSelector, setShowAssetSelector] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  const [scanHistory, setScanHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [processingProposal, setProcessingProposal] = useState(null); // proposal_id being processed

  // Paper/Real choice modal state (opened when user clicks "Accepter")
  const [acceptModal, setAcceptModal] = useState({ open: false, proposalId: null, symbol: null });

  // Accept a proposal - creates the trade (with paper/real choice)
  const handleAcceptProposal = async (proposalId, symbol, isPaper = true) => {
    try {
      setProcessingProposal(proposalId);
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/api/autoscan/accept-proposal`,
        { proposal_id: proposalId, is_paper: isPaper },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        const modeLabel = isPaper ? 'Paper' : 'Réel';
        toast.success(`✅ Trade ${symbol} créé (${res.data.status === 'pending' ? 'En attente' : 'En cours'} · ${modeLabel})`);
        // Update local state
        setScanHistory(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            assets_scanned: prev.assets_scanned.map(asset =>
              asset.proposal_id === proposalId
                ? { ...asset, proposal_status: 'accepted', trade_id: res.data.trade_id, accepted_mode: isPaper ? 'paper' : 'real' }
                : asset
            )
          };
        });
      }
    } catch (error) {
      console.error('Error accepting proposal:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'acceptation');
    } finally {
      setProcessingProposal(null);
      setAcceptModal({ open: false, proposalId: null, symbol: null });
    }
  };

  // Open Paper/Real choice modal
  const openAcceptModal = (proposalId, symbol) => {
    setAcceptModal({ open: true, proposalId, symbol });
  };
  
  // Reject a proposal
  const handleRejectProposal = async (proposalId, symbol) => {
    try {
      setProcessingProposal(proposalId);
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/api/autoscan/reject-proposal`,
        { proposal_id: proposalId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.success) {
        toast.info(`Trade ${symbol} rejeté`);
        // Update local state
        setScanHistory(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            assets_scanned: prev.assets_scanned.map(asset => 
              asset.proposal_id === proposalId 
                ? { ...asset, proposal_status: 'rejected' }
                : asset
            )
          };
        });
      }
    } catch (error) {
      console.error('Error rejecting proposal:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du rejet');
    } finally {
      setProcessingProposal(null);
    }
  };
  
  // Fetch scan history
  const fetchScanHistory = useCallback(async () => {
    if (!isAuthenticated) {
      setLoadingHistory(false);
      return;
    }
    try {
      setLoadingHistory(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/autoscan/history?limit=1`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.history && res.data.history.length > 0) {
        const lastScan = res.data.history[0];
        // Check if scan is less than 24h old
        const scanDate = new Date(lastScan.created_at);
        const now = new Date();
        const hoursAgo = (now - scanDate) / (1000 * 60 * 60);
        
        if (hoursAgo < 24) {
          setScanHistory(lastScan);
        } else {
          setScanHistory(null);
        }
      }
    } catch (error) {
      console.error('Error fetching scan history:', error);
    } finally {
      setLoadingHistory(false);
    }
  }, [isAuthenticated]);
  
  const fetchData = useCallback(async () => {
    if (!isAuthenticated) {
      // Guest: keep default settings, don't hit protected endpoints
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      try {
        const autoScanRes = await axios.get(`${API}/api/autoscan/settings`, { headers });
        setAutoScanSettings(autoScanRes.data);
        setSelectedAssets(autoScanRes.data.selected_assets || []);
      } catch (e) {
        // Auto-scan settings not available - use defaults
      }
      
      try {
        const watchlistRes = await axios.get(`${API}/api/watchlist/symbols`, { headers });
        setAvailableAssets(watchlistRes.data.symbols || []);
      } catch (e) {
        // Watchlist not available
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchData();
    fetchScanHistory();
  }, [fetchData, fetchScanHistory]);
  
  const handleToggleAutoScan = async (enabled) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/autoscan/toggle?enabled=${enabled}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAutoScanSettings(prev => ({ ...prev, enabled }));
      toast.success(enabled ? 'Scan automatique activé' : 'Scan automatique désactivé');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };
  
  const handleSaveAssets = async () => {
    if (selectedAssets.length > autoScanSettings.max_assets) {
      toast.error(`Maximum ${autoScanSettings.max_assets} assets autorisés`);
      return;
    }
    
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const res = await axios.put(`${API}/api/autoscan/settings`, 
        { 
          assets: selectedAssets,
          entry_type: autoScanSettings.entry_type,
          risk_profile: autoScanSettings.risk_profile,
          trade_type: autoScanSettings.trade_type,
          notify_scan_summary: autoScanSettings.notify_scan_summary,
          margin: autoScanSettings.margin
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success(res.data.message);
      setShowAssetSelector(false);
      fetchData();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };
  
  const handleUpdatePreferences = async (field, value) => {
    try {
      setSavingPrefs(true);
      const token = localStorage.getItem('token');
      
      const updatedSettings = {
        ...autoScanSettings,
        [field]: value
      };
      
      await axios.put(`${API}/api/autoscan/settings`, 
        { 
          assets: autoScanSettings.selected_assets,
          entry_type: updatedSettings.entry_type,
          risk_profile: updatedSettings.risk_profile,
          trade_type: updatedSettings.trade_type,
          notify_scan_summary: updatedSettings.notify_scan_summary,
          margin: updatedSettings.margin
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      setAutoScanSettings(updatedSettings);
      toast.success('Préférences mises à jour');
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSavingPrefs(false);
    }
  };
  
  const handleToggleAsset = (symbol) => {
    if (selectedAssets.includes(symbol)) {
      setSelectedAssets(prev => prev.filter(s => s !== symbol));
    } else {
      if (selectedAssets.length >= autoScanSettings.max_assets) {
        toast.error(`Maximum ${autoScanSettings.max_assets} assets`);
        return;
      }
      setSelectedAssets(prev => [...prev, symbol]);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-8">
      {/* Guest CTA banner — full interface stays visible, actions are member-only */}
      <GuestActionBanner
        title="Le Scan Auto détecte vos setups 24/7"
        description="Aperçu invité. Créez un compte gratuit pour configurer votre watchlist, activer les scans automatiques et recevoir les alertes en temps réel."
      />

      {/* Onboarding Tutorial */}
      <OnboardingTutorial
        pageId="scan-auto"
        slides={SCAN_AUTO_SLIDES}
        storageKey="tutorial_scan_auto_seen"
      />
      
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Zap className="w-6 h-6 text-amber-500" />
              Scan Auto
            </h1>
            <HowItWorks pageKey="scan-auto" />
          </div>
          <p className="text-muted-foreground text-sm mt-1">
            Analyse automatique et alertes de trading
          </p>
        </div>
        <div className="flex items-center gap-2">
          <TradingViewLink label="TradingView" />
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/30">
            <Crown className="w-4 h-4 text-primary" />
            <span className="text-primary text-sm font-medium uppercase">
              {autoScanSettings.tier}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs for Settings and Recap */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="settings" className="flex items-center gap-2" data-testid="tab-settings">
            <Settings className="w-4 h-4" />
            Paramètres
          </TabsTrigger>
          <TabsTrigger value="recap" className="flex items-center gap-2" data-testid="tab-recap">
            <History className="w-4 h-4" />
            Récap du jour
            {scanHistory && scanHistory.summary && (
              <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-primary/20 text-primary">
                {scanHistory.summary.total_scanned}
              </span>
            )}
          </TabsTrigger>
        </TabsList>
        
        {/* Settings Tab */}
        <TabsContent value="settings">
          <div className="glass rounded-2xl p-6 border border-border">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Scan Automatique Intraday</h2>
                  <p className="text-muted-foreground text-sm">Tous les jours à 16h00 (heure Paris)</p>
                </div>
              </div>
              <Switch
                checked={autoScanSettings.enabled}
                onCheckedChange={handleToggleAutoScan}
              />
            </div>
        
        <div className="bg-muted/50 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-foreground text-sm">
              Vos assets prioritaires ({selectedAssets.length}/{autoScanSettings.max_assets})
            </span>
            {autoScanSettings.can_modify ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => isAuthenticated ? setShowAssetSelector(true) : navigate('/subscribe')}
                className="text-xs"
                data-testid="scan-auto-modify-assets-btn"
              >
                <Settings className="w-3 h-3 mr-1" />
                Modifier
              </Button>
            ) : (
              <div className="flex items-center gap-2 text-amber-400 text-xs">
                <Lock className="w-3 h-3" />
                Modif. dans {autoScanSettings.days_until_modification}j
              </div>
            )}
          </div>
          
          {selectedAssets.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {selectedAssets.map(asset => {
                const assetName = getSymbolName(asset);
                const ticker = asset.includes(':') ? asset.split(':')[1] : asset;
                return (
                  <div key={asset} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/10 border border-primary/30" title={asset}>
                    <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                    <div className="flex flex-col">
                      <span className="text-foreground text-sm font-medium">{assetName}</span>
                      <span className="text-[10px] text-muted-foreground font-mono">{ticker}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6">
              <Star className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground text-sm">Aucun asset sélectionné</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => isAuthenticated ? setShowAssetSelector(true) : navigate('/subscribe')}
                className="mt-3"
                data-testid="scan-auto-select-assets-btn"
              >
                Sélectionner mes assets
              </Button>
            </div>
          )}
        </div>
        
        <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
          <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-foreground">
            <p className="mb-1"><strong>Comment ça marche ?</strong></p>
            <ul className="space-y-1 text-muted-foreground">
              <li>• Scan IA automatique à 16h00 (30 min après ouverture US)</li>
              <li>• Création de trades INTRADAY ou SWING en attente si opportunité</li>
              <li>• Notification push du résultat du scan (trades créés ou non)</li>
            </ul>
          </div>
        </div>
        
        {/* Weekend Warning for Stock Assets */}
        {autoScanSettings.weekend_warning && (
          <div className={`mt-4 p-4 rounded-xl flex items-start gap-3 ${
            autoScanSettings.weekend_warning.includes('fermés') 
              ? 'bg-amber-500/10 border border-amber-500/30' 
              : 'bg-blue-500/10 border border-blue-500/30'
          }`}>
            {autoScanSettings.weekend_warning.includes('fermés') ? (
              <Calendar className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            )}
            <p className="text-sm text-foreground">{autoScanSettings.weekend_warning}</p>
          </div>
        )}
      </div>
      
      {/* Preferences Section */}
      <div className="glass rounded-2xl p-6 border border-border">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
            <Settings className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Préférences d&apos;analyse</h2>
            <p className="text-muted-foreground text-sm">Personnalisez vos scans automatiques</p>
          </div>
        </div>
        
        <div className="space-y-6">
          {/* Entry Type Selection */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
            <div className="flex items-center gap-3">
              <Target className="w-5 h-5 text-emerald-400" />
              <div>
                <p className="text-foreground text-sm font-medium">Type d&apos;entrée</p>
                <p className="text-muted-foreground text-xs">Comment entrer en position</p>
              </div>
            </div>
            <Select 
              value={autoScanSettings.entry_type} 
              onValueChange={(value) => handleUpdatePreferences('entry_type', value)}
              disabled={savingPrefs}
            >
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="optimal">
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-emerald-400" />
                    <span>Entrée Optimale</span>
                  </div>
                </SelectItem>
                <SelectItem value="market">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-blue-400" />
                    <span>Au Marché</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="px-4 py-2 text-xs text-muted-foreground">
            <strong>Entrée Optimale</strong> : Attend un meilleur prix (zone OTE/discount). Trade créé en &quot;pending&quot;.<br/>
            <strong>Au Marché</strong> : Entre immédiatement au prix actuel. Trade activé directement.
          </div>
          
          {/* Risk Profile Selection */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <div>
                <p className="text-foreground text-sm font-medium">Profil de risque</p>
                <p className="text-muted-foreground text-xs">Niveau de risque des trades</p>
              </div>
            </div>
            <Select 
              value={autoScanSettings.risk_profile} 
              onValueChange={(value) => handleUpdatePreferences('risk_profile', value)}
              disabled={savingPrefs}
            >
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="prudent">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                    <span>Prudent</span>
                  </div>
                </SelectItem>
                <SelectItem value="balanced">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                    <span>Équilibré</span>
                  </div>
                </SelectItem>
                <SelectItem value="dynamic">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-red-500"></span>
                    <span>Dynamique</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="px-4 py-2 text-xs text-muted-foreground grid grid-cols-3 gap-2">
            <div>
              <span className="text-green-400 font-medium">Prudent</span><br/>
              Levier 10x • R:R 2:1
            </div>
            <div>
              <span className="text-amber-400 font-medium">Équilibré</span><br/>
              Levier 20x • R:R 3:1
            </div>
            <div>
              <span className="text-red-400 font-medium">Dynamique</span><br/>
              Levier 30x • R:R 5:1
            </div>
          </div>
          
          {/* Trade Type Selection */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-blue-400" />
              <div>
                <p className="text-foreground text-sm font-medium">Type de trade</p>
                <p className="text-muted-foreground text-xs">Horizon temporel des trades</p>
              </div>
            </div>
            <Select 
              value={autoScanSettings.trade_type || 'INTRADAY'} 
              onValueChange={(value) => handleUpdatePreferences('trade_type', value)}
              disabled={savingPrefs}
              data-testid="trade-type-select"
            >
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="INTRADAY">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-amber-400" />
                    <span>Intraday</span>
                  </div>
                </SelectItem>
                <SelectItem value="SWING">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-purple-400" />
                    <span>Swing</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="px-4 py-2 text-xs text-muted-foreground">
            <strong>Intraday</strong> : Trades à court terme, fermés dans la journée.<br/>
            <strong>Swing</strong> : Trades à moyen terme, tenus plusieurs jours.
          </div>
          
          {/* Notification Toggle */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-blue-400" />
              <div>
                <p className="text-foreground text-sm font-medium">Notification résumé quotidien</p>
                <p className="text-muted-foreground text-xs">Recevoir le résultat du scan (même si 0 trades)</p>
              </div>
            </div>
            <Switch 
              checked={autoScanSettings.notify_scan_summary !== false}
              onCheckedChange={(checked) => handleUpdatePreferences('notify_scan_summary', checked)}
              disabled={savingPrefs}
              data-testid="notify-scan-summary-toggle"
            />
          </div>
          
          {/* Margin Setting */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
            <div className="flex items-center gap-3">
              <DollarSign className="w-5 h-5 text-green-400" />
              <div>
                <p className="text-foreground text-sm font-medium">Marge par trade</p>
                <p className="text-muted-foreground text-xs">Montant investi sur chaque trade (min. 10$, max. 10 000$)</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">$</span>
              <input 
                type="number"
                min="10"
                max="10000"
                step="10"
                value={autoScanSettings.margin || 100}
                onChange={(e) => {
                  const value = Math.max(10, Math.min(10000, parseFloat(e.target.value) || 100));
                  setAutoScanSettings(prev => ({ ...prev, margin: value }));
                }}
                onBlur={(e) => {
                  const value = Math.max(10, Math.min(10000, parseFloat(e.target.value) || 100));
                  handleUpdatePreferences('margin', value);
                }}
                className="w-24 px-3 py-1.5 text-right bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                disabled={savingPrefs}
                data-testid="margin-input"
              />
            </div>
          </div>
        </div>
      </div>
        </TabsContent>
        
        {/* Recap Tab */}
        <TabsContent value="recap">
          <div className="glass rounded-2xl p-6 border border-border">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
                <History className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">Récap du dernier scan</h2>
                {scanHistory ? (
                  <p className="text-muted-foreground text-sm">
                    {new Date(scanHistory.created_at).toLocaleDateString('fr-FR', { 
                      weekday: 'long', 
                      day: 'numeric', 
                      month: 'long',
                      hour: '2-digit',
                      minute: '2-digit'
                    })} • {scanHistory.scan_type?.toUpperCase() || 'INTRADAY'}
                  </p>
                ) : (
                  <p className="text-muted-foreground text-sm">Aucun scan dans les dernières 24h</p>
                )}
              </div>
            </div>
            
            {loadingHistory ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
              </div>
            ) : scanHistory && scanHistory.assets_scanned ? (
              <>
                {/* Summary */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                  <div className="p-3 rounded-lg bg-muted/50 text-center">
                    <p className="text-2xl font-bold text-foreground">{scanHistory.summary?.total_scanned || 0}</p>
                    <p className="text-xs text-muted-foreground">Analysés</p>
                  </div>
                  <div className="p-3 rounded-lg bg-blue-500/10 text-center">
                    <p className="text-2xl font-bold text-blue-400">
                      {scanHistory.summary?.proposals || scanHistory.summary?.trades_created || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">Propositions</p>
                  </div>
                  <div className="p-3 rounded-lg bg-red-500/10 text-center">
                    <p className="text-2xl font-bold text-red-400">{scanHistory.summary?.rejected || 0}</p>
                    <p className="text-xs text-muted-foreground">Non éligibles</p>
                  </div>
                  <div className="p-3 rounded-lg bg-amber-500/10 text-center">
                    <p className="text-2xl font-bold text-amber-400">{scanHistory.summary?.skipped_weekend || 0}</p>
                    <p className="text-xs text-muted-foreground">Week-end</p>
                  </div>
                </div>
                
                {/* Accepted/Rejected counters */}
                {scanHistory.assets_scanned?.some(a => a.proposal_status === 'accepted' || a.proposal_status === 'rejected') && (
                  <div className="flex items-center gap-4 mb-4 p-3 rounded-lg bg-muted/30">
                    <span className="text-sm text-muted-foreground">Statut :</span>
                    <span className="flex items-center gap-1 text-sm text-green-400">
                      <Check className="w-4 h-4" />
                      {scanHistory.assets_scanned.filter(a => a.proposal_status === 'accepted').length} accepté(s)
                    </span>
                    <span className="flex items-center gap-1 text-sm text-red-400">
                      <X className="w-4 h-4" />
                      {scanHistory.assets_scanned.filter(a => a.proposal_status === 'rejected').length} rejeté(s)
                    </span>
                    <span className="flex items-center gap-1 text-sm text-blue-400">
                      <Clock className="w-4 h-4" />
                      {scanHistory.assets_scanned.filter(a => a.proposal_status === 'proposed' && a.eligible).length} en attente
                    </span>
                  </div>
                )}
                
                {/* Asset Details */}
                <div className="space-y-3">
                  {scanHistory.assets_scanned.map((asset, index) => (
                    <div
                      key={asset.proposal_id || index}
                      className={`p-4 rounded-xl border transition-all ${
                        asset.proposal_status === 'accepted'
                          ? 'bg-green-500/5 border-green-500/30' 
                          : asset.proposal_status === 'rejected'
                          ? 'bg-red-500/5 border-red-500/20 opacity-60'
                          : asset.eligible || asset.trade_created
                          ? 'bg-blue-500/5 border-blue-500/30'
                          : 'bg-muted/30 border-border/50'
                      }`}
                      data-testid={`scan-result-${asset.symbol}`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        {/* Asset Info */}
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            asset.direction === 'LONG' 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            {asset.direction === 'LONG' 
                              ? <ArrowUpRight className="w-5 h-5" /> 
                              : <ArrowDownRight className="w-5 h-5" />
                            }
                          </div>
                          <div>
                            <p className="font-semibold text-foreground">
                              {getSymbolName(asset.original_symbol || asset.symbol) || asset.symbol}
                            </p>
                            <p className="text-xs text-muted-foreground font-mono">
                              {asset.direction} • {asset.symbol}
                            </p>
                          </div>
                        </div>
                        
                        {/* R:R Badge */}
                        <div className="flex items-center gap-3 flex-wrap">
                          {asset.market_rr !== null && asset.market_rr !== undefined ? (
                            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-mono text-sm font-bold ${
                              asset.market_rr >= 2.0 
                                ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                                : 'bg-red-500/20 text-red-400 border border-red-500/30'
                            }`}>
                              R:R {asset.market_rr?.toFixed(1)}
                              {asset.market_rr >= 2.0 ? (
                                <CheckCircle2 className="w-4 h-4" />
                              ) : (
                                <XCircle className="w-4 h-4" />
                              )}
                            </div>
                          ) : asset.optimal_rr ? (
                            <div className="px-3 py-1.5 rounded-lg bg-blue-500/20 text-blue-400 border border-blue-500/30 font-mono text-sm font-bold">
                              R:R {asset.optimal_rr?.toFixed(1)} (optimal)
                            </div>
                          ) : null}
                          
                          {/* Status Badge - NEW: Proposal status system */}
                          {asset.proposal_status === 'accepted' ? (
                            <span className="px-3 py-1.5 rounded-lg bg-green-500/20 text-green-400 text-sm font-medium flex items-center gap-1">
                              <CheckCircle2 className="w-4 h-4" />
                              Accepté
                            </span>
                          ) : asset.proposal_status === 'rejected' ? (
                            <span className="px-3 py-1.5 rounded-lg bg-red-500/20 text-red-400 text-sm font-medium flex items-center gap-1">
                              <XCircle className="w-4 h-4" />
                              Rejeté
                            </span>
                          ) : asset.eligible || asset.trade_created ? (
                            // Eligible proposal - show accept/reject buttons
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openAcceptModal(asset.proposal_id, asset.symbol)}
                                disabled={processingProposal === asset.proposal_id}
                                className="bg-green-500/10 border-green-500/30 text-green-400 hover:bg-green-500/20 hover:text-green-300"
                                data-testid={`accept-proposal-${asset.symbol}`}
                              >
                                {processingProposal === asset.proposal_id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Check className="w-4 h-4 mr-1" />
                                    Accepter
                                  </>
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRejectProposal(asset.proposal_id, asset.symbol)}
                                disabled={processingProposal === asset.proposal_id}
                                className="bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20 hover:text-red-300"
                                data-testid={`reject-proposal-${asset.symbol}`}
                              >
                                {processingProposal === asset.proposal_id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <X className="w-4 h-4 mr-1" />
                                    Rejeter
                                  </>
                                )}
                              </Button>
                            </div>
                          ) : (
                            // Not eligible
                            <span className="px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 text-sm font-medium flex items-center gap-1">
                              <XCircle className="w-4 h-4" />
                              Non éligible
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* AI Note / Rejection Reason */}
                      <div className="mt-3 p-3 rounded-lg bg-background/50 border border-border/30">
                        <p className="text-sm text-muted-foreground">
                          {asset.rejection_reason || asset.reason || "Analyse IA non disponible"}
                        </p>
                        {asset.current_price && asset.stop_loss && (
                          <p className="text-xs text-muted-foreground/70 mt-1 font-mono">
                            Prix: ${asset.current_price?.toFixed(2)} • SL: ${asset.stop_loss?.toFixed(2)} • TP: ${asset.tp100?.toFixed(2)}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <Clock className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
                <p className="text-muted-foreground">Aucun scan effectué dans les dernières 24h</p>
                <p className="text-sm text-muted-foreground/70 mt-1">
                  Le prochain scan aura lieu à 16h00 (heure Paris)
                </p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Upgrade Section for Pro users */}
      {autoScanSettings.tier === 'pro' && (
        <div className="glass rounded-2xl p-4 border border-purple-500/20 bg-gradient-to-r from-purple-500/10 to-pink-500/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Crown className="w-5 h-5 text-purple-400" />
              <div>
                <p className="text-foreground text-sm font-medium">Passez en Expert</p>
                <p className="text-muted-foreground text-xs">10 assets + modifications illimitées</p>
              </div>
            </div>
            <Button size="sm" className="bg-gradient-to-r from-purple-500 to-pink-500" onClick={() => navigate('/subscribe')}>
              Upgrade
            </Button>
          </div>
        </div>
      )}
      
      <Dialog open={showAssetSelector} onOpenChange={setShowAssetSelector}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Sélectionner vos assets</DialogTitle>
            <DialogDescription>
              Choisissez jusqu&apos;à {autoScanSettings.max_assets} assets pour le scan automatique.
              {autoScanSettings.tier === 'pro' && (
                <span className="text-amber-400 block mt-1">Modification possible tous les 14 jours.</span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            <div className="space-y-2">
              {(() => {
                // Sort by category then alphabetically (same as Dashboard)
                const categoryOrder = ['crypto', 'stocks', 'etf', 'forex', 'indices', 'commodities', 'commodity', 'custom'];
                const sortedAssets = [...availableAssets].sort((a, b) => {
                  const catA = categoryOrder.indexOf(a.category?.toLowerCase() || 'custom');
                  const catB = categoryOrder.indexOf(b.category?.toLowerCase() || 'custom');
                  if (catA !== catB) return catA - catB;
                  // Same category: sort alphabetically by name
                  return (a.name || a.symbol).localeCompare(b.name || b.symbol);
                });
                
                let currentCategory = null;
                return sortedAssets.map((asset, index) => {
                  const isSelected = selectedAssets.includes(asset.symbol);
                  const showCategoryHeader = asset.category !== currentCategory;
                  currentCategory = asset.category;
                  
                  return (
                    <div key={asset.symbol || index}>
                      {showCategoryHeader && (
                        <div className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-lg mb-2 ${index > 0 ? 'mt-4' : ''} ${
                          asset.category === 'crypto' ? 'bg-orange-500/10 text-orange-400' :
                          asset.category === 'stocks' ? 'bg-green-500/10 text-green-400' :
                          asset.category === 'etf' ? 'bg-purple-500/10 text-purple-400' :
                          asset.category === 'forex' ? 'bg-blue-500/10 text-blue-400' :
                          asset.category === 'indices' ? 'bg-cyan-500/10 text-cyan-400' :
                          asset.category === 'commodities' || asset.category === 'commodity' ? 'bg-yellow-500/10 text-yellow-400' :
                          'bg-gray-500/10 text-gray-400'
                        }`}>
                          {asset.category === 'crypto' ? '🪙 Crypto' :
                           asset.category === 'stocks' ? '📈 Actions' :
                           asset.category === 'etf' ? '📊 ETF' :
                           asset.category === 'forex' ? '💱 Forex' :
                           asset.category === 'indices' ? '📉 Indices' :
                           asset.category === 'commodities' || asset.category === 'commodity' ? '🛢️ Matières Premières' :
                           '📌 Autre'}
                        </div>
                      )}
                      <div
                        onClick={() => handleToggleAsset(asset.symbol)}
                        className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${isSelected ? 'bg-primary/20 border border-primary/50' : 'bg-white/5 border border-transparent hover:bg-white/10'}`}
                      >
                        <div className="flex items-center gap-3">
                          <Star className={`w-5 h-5 ${isSelected ? 'text-amber-400 fill-amber-400' : 'text-gray-600'}`} />
                          <div>
                            <p className="text-white font-medium">{asset.name || asset.symbol.split(':').pop()}</p>
                            <p className="text-gray-500 text-xs">{asset.symbol.split(':').pop()}</p>
                          </div>
                        </div>
                        {isSelected && (
                          <CheckCircle2 className="w-5 h-5 text-primary" />
                        )}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
            
            {availableAssets.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500">Aucun asset dans votre watchlist.</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate('/watchlist')}>
                  Ajouter des assets
                </Button>
              </div>
            )}
          </div>
          
          <DialogFooter className="border-t border-white/10 pt-4">
            <div className="flex items-center justify-between w-full">
              <span className="text-sm text-gray-400">{selectedAssets.length}/{autoScanSettings.max_assets} sélectionné(s)</span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowAssetSelector(false)}>Annuler</Button>
                <Button onClick={handleSaveAssets} disabled={saving || selectedAssets.length === 0}>
                  {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Paper / Réel choice modal (opened when accepting a proposal from Récap) */}
      <Dialog
        open={acceptModal.open}
        onOpenChange={(open) => { if (!open) setAcceptModal({ open: false, proposalId: null, symbol: null }); }}
      >
        <DialogContent className="max-w-md" data-testid="accept-mode-dialog">
          <DialogHeader>
            <DialogTitle>Accepter {acceptModal.symbol}</DialogTitle>
            <DialogDescription>
              Comment veux-tu enregistrer ce trade ?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <button
              type="button"
              data-testid="accept-mode-paper"
              disabled={processingProposal === acceptModal.proposalId}
              onClick={() => handleAcceptProposal(acceptModal.proposalId, acceptModal.symbol, true)}
              className="w-full text-left p-4 rounded-lg border border-blue-500/40 bg-blue-500/5 hover:bg-blue-500/10 transition disabled:opacity-60"
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="text-blue-400 font-semibold">📝 Paper</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 font-mono">SANS RISQUE</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Trade fictif — pour tester la stratégie sans engager de capital.
                Suivi automatique SL/TP inclus. Rangé dans l'onglet <strong>Paper</strong> de ton historique.
              </p>
            </button>
            <button
              type="button"
              data-testid="accept-mode-real"
              disabled={processingProposal === acceptModal.proposalId}
              onClick={() => handleAcceptProposal(acceptModal.proposalId, acceptModal.symbol, false)}
              className="w-full text-left p-4 rounded-lg border border-emerald-500/40 bg-emerald-500/5 hover:bg-emerald-500/10 transition disabled:opacity-60"
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="text-emerald-400 font-semibold">💰 Réel</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 font-mono">CAPITAL RÉEL</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Trade que tu passes chez ton broker — compté dans les stats de ton onglet <strong>Réel</strong> (WR, PnL, ROI).
                Suivi automatique SL/TP inclus.
              </p>
            </button>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setAcceptModal({ open: false, proposalId: null, symbol: null })}
              disabled={processingProposal === acceptModal.proposalId}
              data-testid="accept-mode-cancel"
            >
              Annuler
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
